/**
 * Deal Box File Card Component
 * Displays individual file with actions
 */

import { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { DOCUMENT_TYPE_LABELS, CATEGORY_LABELS, ROLE_LABELS } from '@/constants/deal-box.constants';
import type { DealFile } from '@/types/deal-box.types';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  FileText, 
  FileSpreadsheet, 
  FileImage, 
  File,
  Download, 
  Share2, 
  Trash2, 
  MoreVertical,
  RefreshCw,
  History,
  Eye,
  Lock
} from 'lucide-react';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';

interface DealBoxFileCardProps {
  file: DealFile;
  compact?: boolean;
  onDownload: (file: DealFile) => void;
  onDelete: (file: DealFile) => void;
  onShare?: (file: DealFile) => void;
  onReplace?: (file: DealFile) => void;
  onViewHistory?: (file: DealFile) => void;
}

// Get file icon based on type
function getFileIcon(fileType: string) {
  if (fileType.includes('pdf') || fileType.includes('word') || fileType.includes('document')) {
    return <FileText className="h-8 w-8 text-red-500" />;
  }
  if (fileType.includes('sheet') || fileType.includes('excel') || fileType.includes('csv')) {
    return <FileSpreadsheet className="h-8 w-8 text-green-500" />;
  }
  if (fileType.includes('image')) {
    return <FileImage className="h-8 w-8 text-blue-500" />;
  }
  return <File className="h-8 w-8 text-gray-500" />;
}

// Format file size
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

export function DealBoxFileCard({
  file,
  compact = false,
  onDownload,
  onDelete,
  onShare,
  onReplace,
  onViewHistory,
}: DealBoxFileCardProps) {
  const { language } = useLanguage();
  const docTypeLabels = DOCUMENT_TYPE_LABELS[language as 'en' | 'fr'] || DOCUMENT_TYPE_LABELS.en;
  const categoryLabels = CATEGORY_LABELS[language as 'en' | 'fr'] || CATEGORY_LABELS.en;
  const roleLabels = ROLE_LABELS[language as 'en' | 'fr'] || ROLE_LABELS.en;
  const dateLocale = language === 'fr' ? fr : enUS;

  if (compact) {
    return (
      <div className="flex items-center gap-3 p-2 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
        {getFileIcon(file.fileType)}
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate text-sm">{file.title}</p>
          <p className="text-xs text-muted-foreground">
            {docTypeLabels[file.documentType]} • v{file.version}
          </p>
        </div>
        <Button variant="ghost" size="icon" onClick={() => onDownload(file)}>
          <Download className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {/* File Icon */}
          <div className="flex-shrink-0 p-2 bg-muted rounded-lg">
            {getFileIcon(file.fileType)}
          </div>

          {/* File Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h4 className="font-semibold truncate">{file.title}</h4>
                <p className="text-sm text-muted-foreground truncate">
                  {file.originalFilename}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline">{docTypeLabels[file.documentType]}</Badge>
                <Badge variant="secondary">v{file.version}</Badge>
              </div>
            </div>

            {/* Meta Info */}
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
              <span>{categoryLabels[file.category]}</span>
              <span>{formatFileSize(file.fileSize)}</span>
              <span>
                {format(file.uploadedAt, 'PPp', { locale: dateLocale })}
              </span>
              <span>
                {language === 'fr' ? 'Par' : 'By'} {roleLabels[file.uploadedByRole]}
              </span>
            </div>

            {/* Visibility */}
            <div className="flex items-center gap-2 mt-2">
              <Lock className="h-3 w-3 text-muted-foreground" />
              <div className="flex gap-1">
                {file.visibilityRoles.slice(0, 4).map(role => (
                  <Badge key={role} variant="outline" className="text-xs py-0">
                    {roleLabels[role]}
                  </Badge>
                ))}
                {file.visibilityRoles.length > 4 && (
                  <Badge variant="outline" className="text-xs py-0">
                    +{file.visibilityRoles.length - 4}
                  </Badge>
                )}
              </div>
            </div>

            {file.description && (
              <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                {file.description}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" onClick={() => onDownload(file)}>
              <Download className="h-4 w-4" />
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onDownload(file)}>
                  <Download className="h-4 w-4 mr-2" />
                  {language === 'fr' ? 'Télécharger' : 'Download'}
                </DropdownMenuItem>
                
                {onShare && (
                  <DropdownMenuItem onClick={() => onShare(file)}>
                    <Share2 className="h-4 w-4 mr-2" />
                    {language === 'fr' ? 'Partager' : 'Share'}
                  </DropdownMenuItem>
                )}
                
                {onReplace && (
                  <DropdownMenuItem onClick={() => onReplace(file)}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    {language === 'fr' ? 'Remplacer' : 'Replace'}
                  </DropdownMenuItem>
                )}
                
                {onViewHistory && file.version > 1 && (
                  <DropdownMenuItem onClick={() => onViewHistory(file)}>
                    <History className="h-4 w-4 mr-2" />
                    {language === 'fr' ? 'Historique' : 'History'}
                  </DropdownMenuItem>
                )}
                
                <DropdownMenuSeparator />
                
                <DropdownMenuItem 
                  onClick={() => onDelete(file)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {language === 'fr' ? 'Supprimer' : 'Delete'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
